import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowLeftRight, Bed, Car, Ruler, Lock, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import PropertyImage from "@/components/PropertyImage";
import { Button } from "@/components/ui/button";

const listings = [
  {
    id: 1,
    title: "Apartamento 3 quartos - Vista mar",
    image: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=500&h=350&fit=crop",
    type: "Apartamento",
    location: "Copacabana, Rio de Janeiro - RJ",
    value: "R$ 1.200.000",
    bedrooms: 3,
    parking: 2,
    area: "120m²",
    wantsInReturn: "Casa em condomínio — Niterói ou Região dos Lagos",
  },
  {
    id: 2,
    title: "Casa em condomínio fechado",
    image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500&h=350&fit=crop",
    type: "Casa",
    location: "Alphaville, Campinas - SP",
    value: "R$ 1.800.000",
    bedrooms: 4,
    parking: 3,
    area: "280m²",
    wantsInReturn: "Apartamento alto padrão — Centro de São Paulo",
  },
  {
    id: 3,
    title: "Terreno 500m² em condomínio",
    image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=500&h=350&fit=crop",
    type: "Terreno",
    location: "Betel, Paulínia - SP",
    value: "R$ 450.000",
    bedrooms: null,
    parking: null,
    area: "500m²",
    wantsInReturn: "Apartamento em Campinas — aceita troco",
  },
  {
    id: 4,
    title: "Cobertura duplex reformada",
    image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=500&h=350&fit=crop",
    type: "Apartamento",
    location: "Savassi, Belo Horizonte - MG",
    value: "R$ 2.100.000",
    bedrooms: 4,
    parking: 3,
    area: "230m²",
    wantsInReturn: "Casa ou chácara — região de BH",
  },
  {
    id: 5,
    title: "Sala comercial 80m²",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=500&h=350&fit=crop",
    type: "Comercial",
    location: "Batel, Curitiba - PR",
    value: "R$ 650.000",
    bedrooms: null,
    parking: 1,
    area: "80m²",
    wantsInReturn: "Apartamento residencial de valor equivalente",
  },
  {
    id: 6,
    title: "Casa térrea com piscina",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&h=350&fit=crop",
    type: "Casa",
    location: "Jurerê, Florianópolis - SC",
    value: "R$ 3.200.000",
    bedrooms: 5,
    parking: 4,
    area: "350m²",
    wantsInReturn: "Imóvel em São Paulo capital — valor similar",
  },
];

const FeaturedListings = () => {
  const VISIBLE_COUNT = 4;
  const TOTAL_ESTIMATE = "+140";
  return (
    <section id="destaques" className="py-20 bg-secondary/30">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-end justify-between mb-12"
        >
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
              Imóveis disponíveis para permuta
            </h2>
            <p className="text-muted-foreground font-body text-lg">
              Encontre a troca ideal para o seu imóvel
            </p>
            <Badge className="mt-3 bg-accent/10 text-accent border border-accent/20 font-display">
              <Sparkles size={12} className="mr-1" /> {TOTAL_ESTIMATE} imóveis disponíveis
            </Badge>
          </div>
          <Link to="/signup" className="hidden md:block text-accent font-display font-semibold hover:underline">
            Ver todos →
          </Link>
        </motion.div>

        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((item, i) => (
            <motion.article
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              whileHover={{ y: -6 }}
              className={`group relative rounded-2xl overflow-hidden bg-card shadow-card hover:shadow-card-hover transition-all duration-300 border border-border/50 cursor-pointer ${
                i >= VISIBLE_COUNT ? "select-none" : ""
              }`}
            >
              <div className="relative h-52 overflow-hidden">
                <PropertyImage
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full"
                  imgClassName="group-hover:scale-105 transition-transform duration-500"
                  watermarkSize="lg"
                />
                <Badge className="absolute top-3 left-3 bg-primary/90 text-primary-foreground backdrop-blur-sm border-0 font-display text-xs">
                  {item.type}
                </Badge>
                <div className="absolute bottom-3 right-3 bg-card/90 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                  <span className={`font-display font-bold text-foreground text-sm ${i >= VISIBLE_COUNT ? "blur-sm" : ""}`}>
                    {item.value}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <h3 className="font-display font-semibold text-lg text-foreground mb-2 group-hover:text-accent transition-colors line-clamp-1">
                  {item.title}
                </h3>

                <div className={`flex items-center gap-1.5 text-sm text-muted-foreground mb-3 ${i >= VISIBLE_COUNT ? "blur-sm" : ""}`}>
                  <MapPin size={14} className="shrink-0" />
                  <span className="font-body line-clamp-1">{item.location}</span>
                </div>

                {/* Property specs */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                  {item.bedrooms && (
                    <span className="flex items-center gap-1">
                      <Bed size={14} />
                      {item.bedrooms} quartos
                    </span>
                  )}
                  {item.parking && (
                    <span className="flex items-center gap-1">
                      <Car size={14} />
                      {item.parking} vagas
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Ruler size={14} />
                    {item.area}
                  </span>
                </div>

                <div className={`flex items-start gap-2 p-3 rounded-xl bg-accent/8 border border-accent/15 ${i >= VISIBLE_COUNT ? "blur-sm" : ""}`}>
                  <ArrowLeftRight size={16} className="text-accent mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[10px] font-display font-semibold text-accent uppercase tracking-wider">Aceita em troca</span>
                    <p className="text-sm font-body text-foreground/80 line-clamp-1">
                      {item.wantsInReturn}
                    </p>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}

          {/* CTA overlay covering the gated portion */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 top-1/2 flex items-end justify-center bg-gradient-to-b from-transparent via-background/70 to-background">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="pointer-events-auto mb-8 max-w-xl w-[92%] rounded-2xl border border-accent/30 bg-card shadow-card-hover p-6 md:p-7 text-center"
            >
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-accent/10">
                <Lock className="text-accent" size={20} />
              </div>
              <h3 className="font-display font-bold text-xl text-foreground mb-2">
                Desbloqueie todos os imóveis compatíveis
              </h3>
              <p className="text-muted-foreground font-body text-sm mb-5">
                Cadastre seu imóvel e ative buscas automáticas por permuta. O motor cruza a sua carteira com {TOTAL_ESTIMATE} imóveis disponíveis.
              </p>
              <Link to="/signup">
                <Button size="lg" className="font-display">
                  Cadastrar meu imóvel e ver todos
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/signup">
            <Button variant="outline" size="lg" className="font-display">
              Cadastrar meu imóvel e ver todos
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedListings;
